import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Inline filter-chip row — the shared pill-chip control for section-level
 * filters (#625 review). Extracted from HealthPage (the medical hub) so the idiom stops
 * re-growing per page; the older per-page chip rows (TaskFilters,
 * CommunityChrome, TRFerFilterStrip) migrate here in the #635 pass.
 *
 * Active style is deliberately ink-on-ink (`bg-foreground`), not indigo —
 * a filter chip is content, not navigation, and the page's one
 * nav-colored stratum stays its tab row.
 */
export interface FilterChipOption {
  value: string;
  label: string;
  count?: number;
}

export function FilterChips({
  options,
  value,
  onChange,
  children,
}: {
  options: FilterChipOption[];
  value: string;
  onChange: (v: string) => void;
  /** Trailing annotation slot (e.g. a refill note), pushed right by the row. */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((o) => {
        const isActive = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-caption transition-colors',
              isActive
                ? 'bg-foreground text-background border-foreground'
                : 'bg-background text-foreground border-border hover:bg-muted',
            )}
          >
            {o.label}
            {o.count != null && (
              <span className={isActive ? 'text-background/70' : 'text-stevens-ink-3'}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
      {children}
    </div>
  );
}
